<template>
	<q-card>
		<q-toolbar>
			<q-toolbar-title>{{ `${$t('reviews')} - ${title}` }}</q-toolbar-title>
		</q-toolbar>

		<q-separator />

		<q-card-section class="bg-grey-2 q-pa-xs">
			<q-list>
				<q-item>
					<q-item-section>
						<div class="text-center">
							<div class="text-h6">
								{{ state.user_comment ? $t('your_rating') : $t('how_do_you_rate_this_training') }}
							</div>
						</div>

						<div class="self-center no-outline">
							<q-field
								borderless
								:disable="state.user_disable_comment"
								:error="v$.user_stars.$error"
								@input="v$.user_stars.$touch"
								rounded
								:helper="$t('how_do_you_rate_this_training')"
							>
								<template v-slot:control>
									<q-rating
										color="amber"
										v-model="state.user_stars"
										:max="5"
										icon="fas fa-star"
										size="2rem"
									/>
								</template>
							</q-field>
							<q-tooltip
								v-if="state.user_disable_comment"
								anchor="center middle"
								self="center middle"
								class="bg-primary"
								style="font-size: 14px"
							>
								{{ $t(state.motive) }}
							</q-tooltip>
						</div>
					</q-item-section>
				</q-item>

				<q-item class="q-py-xs">
					<q-item-section avatar>
						<q-avatar v-if="user_image">
							<img :src="user_image" />
						</q-avatar>

						<q-btn
							round
							color="blue-grey-9 no-pointer-events"
							size="md"
							:label="labelButton"
							v-else
						/>
					</q-item-section>

					<q-item-section>
						<q-input
							class="q-pt-md"
							autogrow
							rounded
							outlined
							dense
							v-model="state.user_comment"
							:error="v$.user_comment.$error"
							@input="v$.user_comment.$touch"
							bg-color="white"
							color="blue-grey-4"
							type="text-area"
							:placeholder="$t('add_a_review')"
							:disable="state.user_disable_comment"
						>
							<template class="self-center" v-slot:append>
								<q-btn
									class="self-center"
									flat
									round
									dense
									icon="fal fa-paper-plane"
									color="blue-grey-4"
									@click="addReview()"
								/>
							</template>
						</q-input>
					</q-item-section>
				</q-item>
			</q-list>
			<div class="row q-mb-md">
				<div class="col-12 q-mt-sm flex justify-center items-center">
					<q-btn
						color="negative"
						class="q-mr-md q-px-sm"
						:label="$t('close')"
						icon="fal fa-times"
						v-close-popup
					/>
					<q-btn
						:disable="state.user_disable_comment"
						color="positive"
						class="q-px-sm"
						:label="$t('send')"
						icon="fal fa-paper-plane"
						@click="addReview()"
					/>
				</div>
			</div>
		</q-card-section>

		<q-separator />

		<q-card-section class="bg-grey-2 q-pa-xs relative-position" v-if="state.comments.length > 0">
			<q-list padding>
				<q-item
					v-for="(c, index) in state.comments"
					:key="index"
					:class="index + 1 === state.comments.length ? '' : 'q-mb-lg'"
				>
					<q-item-section avatar>
						<q-avatar v-if="c.user_image">
							<img :src="c.user_image" />
						</q-avatar>

						<q-btn
							round
							color="blue-grey-9 no-pointer-events"
							size="md"
							:label="initialUser(c.user_name)"
							v-else
						/>
					</q-item-section>

					<q-item-section>
						<q-item-label lines="1" class="text-weight-medium text-body1">
							{{ c.user_name }}
						</q-item-label>

						<q-item-label sublabel lines="2">
							<p>
								<q-rating
									color="amber"
									v-model="c.stars"
									:max="5"
									icon="fas fa-star"
									:disable="true"
								/>
							</p>
						</q-item-label>

						<q-item-label caption lines="1">
							{{ $dateUtil.fDateText(c.evaluate_date) }}
						</q-item-label>

						<q-item-label class="text-grey-7">
							{{ c.comment }}
						</q-item-label>
					</q-item-section>
				</q-item>

				<q-item v-if="state.pagination.totalPages > 1">
					<q-item-section pagination>
						<q-pagination
							class="flex flex-center"
							direction-links
							v-model="state.pagination.page"
							:max="state.pagination.totalPages"
							@update:model-value="onChangePage"
						/>
					</q-item-section>
				</q-item>
			</q-list>
		</q-card-section>
	</q-card>
</template>

<script setup>
import { ref, reactive, computed, onMounted, defineProps, toRefs } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { date, useQuasar } from 'quasar'
import { useVuelidate } from '@vuelidate/core'
import { required, maxLength, minLength, between } from '@vuelidate/validators'
// composables
import useUserReviews from 'src/composables/useUserReviews'

const route = useRoute()
const store = useStore()
const $q = useQuasar()

const access_token = store.getters['common/access_token']
const full_name = store.getters['common/full_name']
const user_image = store.getters['common/user_image']

const labelButton = computed(() => full_name.charAt(0).toUpperCase())

const props = defineProps({
	module: String,
	moduleData: Object,
})

const { module, moduleData, trailId } = toRefs(props)
const title = ref('')

const state = reactive({
	user_stars: 0,
	user_comment: '',
	user_disable_comment: true,
	comments: [],
	motive: '',
	pagination: {
		page: 1,
		page_size: 5, // Qty comments per page
		totalPages: 1,
	},
})

const initialUser = (user_name) => (user_name ? user_name.charAt(0).toUpperCase() : '')

const { fetchReviews, postReview } = useUserReviews()

const onChangePage = () => fetchComments(module.value, moduleData.value.id)

const getModuleCompleted = () => {
	if (moduleData.value?.user_progress >= 100) return true
	else if (module.value === 'webcasts') {
		return moduleData.value.webcast_user_result_status == 1
	} else if (module.value === 'trails') {
		return (
			moduleData.value.user_result_status === 'approved' ||
			moduleData.value.user_trail_progress >= 100
		)
	} else return false
}

const fetchComments = async (module, id) => {
	$q.loading.show()

	try {
		const { totalPages, comments, userReview } = await fetchReviews({
			module: module,
			page: state.pagination.page,
			page_size: state.pagination.page_size,
			id,
		})

		state.pagination.totalPages = totalPages
		state.comments = comments

		if (userReview?.evaluate_date) {
			state.user_disable_comment = true
			state.motive = 'you_already_reviewed'
			state.user_stars = userReview.stars
			state.user_comment = userReview.comment
		} else {
			if (getModuleCompleted()) state.user_disable_comment = false
			else {
				state.motive = 'only_available_when_finished_training'
				state.user_disable_comment = true
			}
		}
	} catch (e) {
		console.log(e)
	} finally {
		$q.loading.hide()
	}
}

const addReview = async () => {
	v$.value.$touch()

	if (!v$.value.$error) {
		$q.loading.show()

		const data = {
			stars: state.user_stars,
		}

		if (state.user_comment) data.comment = state.user_comment
		if (route.params.id_trail) data.trail_id = route.params.id_trail

		try {
			await postReview({
				module: module.value,
				data,
				id: moduleData.value.id,
			})
		} catch (e) {
			console.log(e)
		} finally {
			fetchComments(module.value, moduleData.value.id)
		}
	}
}

onMounted(() => {
	if (moduleData?.value) {
		title.value = moduleData.value.title
		fetchComments(module.value, moduleData.value.id)
	}
})

const rules = {
	user_stars: { required, between: between(1, 5) },
	user_comment: { minLength: minLength(2), maxLength: maxLength(1024) },
}

const v$ = useVuelidate(rules, state)
</script>

<style></style>
